import { useEffect } from "react";
import { createBrowserRouter, RouterProvider, Navigate, useLocation } from "react-router-dom";
import PMLayout from "./components/layout/PMLayout";
import RequireAuth from "./components/routing/RequireAuth";
import AuthLoading from "./components/common/AuthLoading";
import { useAuth } from "./contexts/AuthContext";
import { useWorkspace } from "./contexts/WorkspaceContext";

// ── your existing pages ──────────────────────────────────────────
import Dashboard        from "./pages/Dashboard";        // or PMDashboard.js
import PMDashboard      from "./pages/PMDashboard";
import MyTasks, { ActorProfilePage, TaskDetailsPage, TaskStagePage } from "./pages/MyTasks";
import TaskCreatePage, {
  CareTaskCreatePage,
  GeneralTaskCreatePage,
  TravoTaskCreatePage,
} from "./pages/TaskCreatePage";
import ActionablesPage  from "./pages/ActionablesPage";
import NotificationsPage from "./pages/NotificationsPage";
import ChatSpace        from "./pages/ChatSpace";
import EmailMessages    from "./pages/EmailMessages";
import AccessControl    from "./pages/AccessControl";
import Tags             from "./pages/Tags";
import Settings         from "./pages/Settings";
import {
  AppDetailsSettingsPage,
  ChangePasswordSettingsPage,
  DataStorageSettingsPage,
  DeleteOrSuspendAccountSettingsPage,
  EmailVerificationSettingsPage,
  LanguageSettingsPage,
  NotificationsSettingsPage,
  PhoneVerificationSettingsPage,
  SecurityQuestionsSettingsPage,
  SupportedLocationsSettingsPage,
  ThemeSettingsPage,
} from "./pages/settings/SettingsDetailPages";
import Feedback         from "./pages/Feedback";
import Help             from "./pages/Help";
import Support          from "./pages/Support";
import ComingSoon       from "./pages/ComingSoon";
import Reports          from "./pages/Reports";
import JobSearch        from "./pages/JobSearch";
import JobOpportunityDetails from "./pages/JobOpportunityDetails";
import JobPosts         from "./pages/JobPosts";
import NewJobPost       from "./pages/NewJobPost";
import JobOpportunityInterviewSessions from "./pages/JobOpportunityInterviewSessions";
import JobApplications  from "./pages/JobApplications";
import JobApplicationDetails from "./pages/JobApplicationDetails";
import TalentSearch     from "./pages/TalentSearch";
import ScheduledInterviews from "./pages/ScheduledInterviews";
import BackgroundChecks from "./pages/BackgroundChecks";
import Invitations from "./pages/Invitations";
import ProfessionalProfiles from "./pages/ProfessionalProfiles";
import ProfessionalProfileDetails from "./pages/ProfessionalProfileDetails";
import WebsiteDetails from "./pages/WebsiteDetails";
import WebsiteTemplates from "./pages/WebsiteTemplates";
import WebsiteBuild from "./pages/WebsiteBuild";
import WebsiteDomain from "./pages/WebsiteDomain";
import WebsiteGenerating from "./pages/WebsiteGenerating";
import WebsiteSuccess from "./pages/WebsiteSuccess";
import Partners         from "./pages/Partners";
import PartnerCommissions from "./pages/PartnerCommissions";
import OrganizationsPage from "./pages/OrganizationsPage";
import OrganizationDetailsPage from "./pages/OrganizationDetailsPage";
import PublicOrganizationDetailsPage from "./pages/PublicOrganizationDetailsPage";
import Users            from "./pages/Users";
import UserFormPage     from "./pages/UserFormPage";
import Profile          from "./pages/Profile";
import WorkspacesPage from "./workspaces/WorkspacesPage";
import WorkspaceCreatePage from "./workspaces/WorkspaceCreatePage";
import WorkspaceDetailsPage from "./workspaces/WorkspaceDetailsPage";
import WorkspaceSubscriptionsPage from "./workspaces/WorkspaceSubscriptionsPage";
import WorkspacePaymentMethodsPage from "./workspaces/WorkspacePaymentMethodsPage";
import WorkspaceOrganizationPage from "./workspaces/WorkspaceOrganizationPage";
import WorkspaceChargesPage from "./workspaces/WorkspaceChargesPage";
import WorkspaceEarningsPage from "./workspaces/WorkspaceEarningsPage";
import WorkspaceMemberDetailsPage from "./workspaces/WorkspaceMemberDetailsPage";
import WorkspaceInvitationAcceptPage from "./workspaces/WorkspaceInvitationAcceptPage";

// project hierarchy pages — already in your pages/ folder
import Projects         from "./pages/Projects";              // list of all projects
import ProjectFormPage  from "./pages/ProjectFormPage";
import ProjectDetails   from "./pages/ProjectDetails";        // iterations inside a project
import IterationDetails from "./pages/IterationDetails";      // tasks inside an iteration
import Iterations       from "./pages/Iterations";            // (keep if you use it standalone)
import TaskDetailPage   from "./pages/TaskDetailPage";
import TaskFormPage     from "./pages/TaskFormPage";
import AuthLogin        from "./pages/AuthLogin";
import AuthHandoff from "./pages/AuthHandoff";
import AuthSignup       from "./pages/AuthSignup";
import AuthLinkedInCallback from "./pages/AuthLinkedInCallback";
import ProductOnboardingPage from "./pages/ProductOnboardingPage";
import AuthAppleSetup   from "./pages/AuthAppleSetup";
import AuthRecoverRequest from './pages/AuthRecoverRequest';
import AuthRecoverVerify from './pages/AuthRecoverVerify';
import AuthRecoverReset from './pages/AuthRecoverReset';
import AuthRecoverSuccess from './pages/AuthRecoverSuccess';
import SafeLinkRedirect from "./pages/SafeLinkRedirect";
import PublicProductPage from "./pages/PublicProductPage";
import AppDownloadPage from "./pages/AppDownloadPage";
import { initGoogleAnalytics, trackGoogleAnalyticsPageView } from "./services/googleAnalytics";

// QA Testable Apps pages
import QaTestAppsPage from "./projects/pages/qa-tests";
import QaTestFeaturesPage from "./projects/pages/qa-tests/FeaturesPage";
import QaTestAppHistoryPage from "./projects/pages/qa-tests/AppHistoryPage";
import QaTestFeatureHistoryPage from "./projects/pages/qa-tests/FeatureHistoryPage";
// ─────────────────────────────────────────────────────────────────

const WorkspacesRedirect = () => {
  const { pathname, search, hash } = useLocation();
  return (
    <Navigate
      to={`${pathname.replace(/^\/product-accounts/, "/workspaces")}${search}${hash}`}
      replace
    />
  );
};

const WorkspaceOrganizationRedirect = () => {
  const { pathname, search, hash } = useLocation();
  return (
    <Navigate
      to={`${pathname.replace(/\/business-entity$/, "/organization")}${search}${hash}`}
      replace
    />
  );
};

const ProfessionalProfileDetailsRoute = () => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <AuthLoading />;
  }

  if (!isAuthenticated) {
    return <ProfessionalProfileDetails />;
  }

  return (
    <PMLayout>
      <ProfessionalProfileDetails />
    </PMLayout>
  );
};

const JobOpportunityDetailsRoute = () => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <AuthLoading />;
  }

  if (!isAuthenticated) {
    return <JobOpportunityDetails />;
  }

  return (
    <PMLayout>
      <JobOpportunityDetails />
    </PMLayout>
  );
};

const OrganizationWorkspaceRoute = ({ children }) => {
  const {
    isOrganizationWorkspace,
    loading,
    selectedWorkspace,
    workspaceListReady,
  } = useWorkspace();

  if (loading || !workspaceListReady) {
    return <AuthLoading />;
  }

  if (selectedWorkspace && !isOrganizationWorkspace) {
    return <Navigate to="/home" replace />;
  }

  return children;
};

const router = createBrowserRouter([
  { path: "/safe-link", element: <SafeLinkRedirect /> },
  { path: "/download", element: <AppDownloadPage /> },
  { path: "/", element: <PublicProductPage /> },
  { path: "/about", element: <PublicProductPage /> },
  { path: "/contact-us", element: <PublicProductPage /> },
  { path: "/contact", element: <Navigate to="/contact-us" replace /> },
  { path: "/privacy-policy", element: <PublicProductPage /> },
  { path: "/privacy", element: <Navigate to="/privacy-policy" replace /> },
  { path: "/help", element: <PublicProductPage /> },
  { path: "/terms-of-use", element: <PublicProductPage /> },
  { path: "/terms", element: <Navigate to="/terms-of-use" replace /> },
  { path: "/faqs", element: <PublicProductPage /> },
  { path: "/faq", element: <Navigate to="/faqs" replace /> },
  { path: "/product", element: <Navigate to="/" replace /> },
  { path: "/product/about", element: <Navigate to="/about" replace /> },
  { path: "/product/contact", element: <Navigate to="/contact-us" replace /> },
  { path: "/product/privacy", element: <Navigate to="/privacy-policy" replace /> },
  { path: "/product/help", element: <Navigate to="/help" replace /> },
  { path: "/product/terms", element: <Navigate to="/terms-of-use" replace /> },
  { path: "/product/faqs", element: <Navigate to="/faqs" replace /> },
  { path: "/login", element: <AuthLogin /> },
  { path: "/auth/handoff", element: <AuthHandoff /> },
  { path: "/get-started", element: <ProductOnboardingPage /> },
  { path: "/auth/landing", element: <Navigate to="/login" replace /> },
  { path: "/auth/login", element: <Navigate to="/login" replace /> },
  { path: "/auth/signup", element: <AuthSignup /> },
  { path: "/auth/linkedin/callback", element: <AuthLinkedInCallback /> },
  { path: "/auth/apple-setup", element: <AuthAppleSetup /> },
  { path: "/auth/social-setup", element: <AuthAppleSetup /> },
  { path: "/auth/forgot-password", element: <Navigate to="/auth/recover" replace /> },
  { path: "/auth/recover", element: <AuthRecoverRequest /> },
  { path: "/auth/verify", element: <AuthRecoverVerify /> },
  { path: "/auth/reset", element: <AuthRecoverReset /> },
  { path: "/auth/recovered", element: <AuthRecoverSuccess /> },
  { path: "/auth/session-expired", element: <AuthLogin /> },
  { path: "/auth/logout", element: <Navigate to="/login" replace /> },
  { path: "/job-opportunities/:jobOpportunityId", element: <JobOpportunityDetailsRoute /> },
  { path: "/professional-profiles/:profileId", element: <ProfessionalProfileDetailsRoute /> },
  { path: "/public/organizations/:organizationId", element: <PublicOrganizationDetailsPage /> },
  {
    path: "/",
    element: (
      <RequireAuth>
        <PMLayout />
      </RequireAuth>
    ),
    children: [

      // overview
      { path: "home",      element: <Dashboard />  },
      { path: "dashboard", element: <PMDashboard /> },
      { path: "chat-space", element: <ChatSpace /> },
      { path: "email-messages", element: <EmailMessages /> },
      { path: "actionables", element: <ActionablesPage /> },
      { path: "notifications/:category", element: <NotificationsPage /> },
      { path: "access-control", element: <AccessControl /> },
      { path: "my-tasks",  element: <MyTasks />    },
      { path: "my-tasks/:taskId",  element: <TaskDetailsPage />    },
      { path: "my-tasks/:taskId/:stage",  element: <TaskStagePage />    },
      { path: "requesters/:actorId",  element: <ActorProfilePage actorType="requester" />    },
      { path: "helpers/:actorId",  element: <ActorProfilePage actorType="helper" />    },
      { path: "my-tasks/new",  element: <TaskCreatePage />    },
      { path: "my-tasks/new/general",  element: <GeneralTaskCreatePage />    },
      { path: "my-tasks/new/travo",  element: <TravoTaskCreatePage />    },
      { path: "my-tasks/new/care",  element: <CareTaskCreatePage />    },
      { path: "job-search", element: <JobSearch /> },
      { path: "job-opportunities/:jobOpportunityId", element: <JobOpportunityDetails /> },
      { path: "job-opportunities/:jobOpportunityId/interview-sessions", element: <JobOpportunityInterviewSessions /> },
      { path: "job-posts", element: <JobPosts /> },
      { path: "job-posts/new", element: <NewJobPost /> },
      { path: "job-applications", element: <JobApplications /> },
      { path: "job-applications/:applicationId", element: <JobApplicationDetails /> },
      { path: "talent-search", element: <TalentSearch /> },
      { path: "interviews", element: <ScheduledInterviews /> },
      { path: "professional-profiles", element: <ProfessionalProfiles /> },
      { path: "professional-profiles/new", element: <ProfessionalProfileDetails forceAddMode /> },
      { path: "professional-profiles/:profileId", element: <ProfessionalProfileDetails /> },
      { path: "professional-profiles/:profileId/edit", element: <ProfessionalProfileDetails startEditing /> },
      { path: "background-checks", element: <BackgroundChecks /> },
      { path: "website", element: <WebsiteDetails /> },
      { path: "website/templates", element: <WebsiteTemplates /> },
      { path: "website/regenerate", element: <WebsiteTemplates regenerate /> },
      { path: "website/build/:templateId", element: <WebsiteBuild /> },
      { path: "website/generating/:websiteId", element: <WebsiteGenerating /> },
      { path: "website/success/:websiteId", element: <WebsiteSuccess /> },
      { path: "website/domain", element: <WebsiteDomain /> },
      { path: "reports", element: <Reports /> },

      // ── projects drill-down hierarchy ──────────────────────────
      { path: "projects",                                                    element: <Projects />         },
      { path: "projects/new",                                               element: <ProjectFormPage />  },
      { path: "projects/:projectId",                                         element: <ProjectDetails />   },
      { path: "projects/:projectId/edit",                                    element: <ProjectFormPage />  },
      { path: "projects/:projectId/iterations",                              element: <Iterations />       },
      { path: "projects/:projectId/iterations/new",                          element: <ProjectDetails />   },
      { path: "projects/:projectId/iterations/:iterationId/edit",            element: <ProjectDetails />   },
      { path: "projects/:projectId/iterations/:iterationId",                 element: <IterationDetails /> },
      { path: "projects/:projectId/iterations/:iterationId/tasks/:taskId",   element: <TaskDetailPage />   },
      { path: "projects/:projectId/iterations/:iterationId/tasks/new",      element: <TaskFormPage />     },
      { path: "projects/:projectId/iterations/:iterationId/tasks/:taskId/edit", element: <TaskFormPage />  },
      // ──────────────────────────────────────────────────────────

      // manage
      { path: "users",     element: <Users />      },
      { path: "users/new",  element: <UserFormPage /> },
      { path: "users/:userId/edit", element: <UserFormPage /> },
      { path: "users/:userId", element: <Profile /> },
      { path: "workspaces", element: <WorkspacesPage /> },
      { path: "organizations", element: <OrganizationWorkspaceRoute><OrganizationsPage /></OrganizationWorkspaceRoute> },
      { path: "organizations/:organizationId", element: <OrganizationWorkspaceRoute><OrganizationDetailsPage /></OrganizationWorkspaceRoute> },
      { path: "invitations", element: <Invitations /> },
      { path: "workspaces/new", element: <WorkspaceCreatePage /> },
      { path: "workspaces/invitations/accept", element: <WorkspaceInvitationAcceptPage /> },
      { path: "workspaces/:workspaceId/invitations/:invitationId", element: <WorkspaceInvitationAcceptPage /> },
      { path: "workspaces/:workspaceId", element: <WorkspaceDetailsPage /> },
      { path: "workspaces/:workspaceId/members/:userId", element: <WorkspaceMemberDetailsPage /> },
      { path: "workspaces/:workspaceId/subscriptions", element: <WorkspaceSubscriptionsPage /> },
      { path: "workspaces/:workspaceId/payment-methods", element: <WorkspacePaymentMethodsPage /> },
      { path: "workspaces/:workspaceId/organization", element: <WorkspaceOrganizationPage /> },
      { path: "workspaces/:workspaceId/business-entity", element: <WorkspaceOrganizationRedirect /> },
      { path: "workspaces/:workspaceId/charges", element: <WorkspaceChargesPage /> },
      { path: "workspaces/:workspaceId/earnings", element: <WorkspaceEarningsPage /> },
      { path: "product-accounts", element: <WorkspacesRedirect /> },
      { path: "product-accounts/*", element: <WorkspacesRedirect /> },
      { path: "partners",  element: <Partners />   },
      { path: "partners/commissions", element: <PartnerCommissions /> },
      { path: "tags",      element: <Tags />       },
      { path: "settings",  element: <Settings />   },
      { path: "settings/change-password", element: <ChangePasswordSettingsPage /> },
      { path: "settings/security-questions", element: <SecurityQuestionsSettingsPage /> },
      { path: "settings/verify-email", element: <EmailVerificationSettingsPage /> },
      { path: "settings/verify-phone", element: <PhoneVerificationSettingsPage /> },
      { path: "settings/app-details", element: <AppDetailsSettingsPage /> },
      { path: "settings/supported-locations", element: <SupportedLocationsSettingsPage /> },
      { path: "settings/delete-account", element: <DeleteOrSuspendAccountSettingsPage /> },
      { path: "settings/notifications", element: <NotificationsSettingsPage /> },
      { path: "settings/language", element: <LanguageSettingsPage /> },
      { path: "settings/theme", element: <ThemeSettingsPage /> },
      { path: "settings/data-storage", element: <DataStorageSettingsPage /> },
      { path: "feedback",  element: <Feedback />   },
      { path: "app/help",  element: <Help />       },
      { path: "support",   element: <Support />    },
      { path: "profile",   element: <Profile />    },

      // qa-test-runs
      { path: "qa-test-runs", element: <QaTestAppsPage /> },
      { path: "qa-test-runs/apps/:appId/features", element: <QaTestFeaturesPage /> },
      { path: "qa-test-runs/apps/:appId/history", element: <QaTestAppHistoryPage /> },
      { path: "qa-test-runs/apps/:appId/features/:featureId/history", element: <QaTestFeatureHistoryPage /> },

      // fallback
      { path: "*",         element: <Navigate to="/home" replace /> },
    ],
  },
]);

export default function App() {
  useEffect(() => {
    if (!initGoogleAnalytics()) {
      return undefined;
    }

    trackGoogleAnalyticsPageView(router.state.location);

    return router.subscribe((state) => {
      trackGoogleAnalyticsPageView(state.location);
    });
  }, []);

  return <RouterProvider router={router} />;
}
